import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Card, Chip, CircularProgress } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EmailIcon from "@mui/icons-material/Email";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { motion } from "framer-motion";
import api from "../../../utils/api";

/* ── Status chip colours ─────────────────────────────────────────────────── */
const STATUS_COLORS = {
  open:        { bg: "rgba(234,88,12,0.12)",  text: "#ea580c" },
  in_progress: { bg: "rgba(37,99,235,0.12)",  text: "#2563eb" },
  pending:     { bg: "rgba(217,119,6,0.12)",  text: "#b45309" },
  resolved:    { bg: "rgba(22,163,74,0.12)",  text: "#16a34a" },
  closed:      { bg: "rgba(107,114,128,0.12)", text: "#6b7280" },
  cancelled:   { bg: "rgba(220,38,38,0.12)",  text: "#dc2626" },
};

const statusChipSx = (status = "") => {
  const key   = status.toLowerCase().replace(/\s+/g, "_");
  const style = STATUS_COLORS[key] || { bg: "rgba(47,99,173,0.1)", text: "#2f63ad" };
  return {
    height: 20,
    borderRadius: "6px",
    fontWeight: 700,
    fontSize: "0.67rem",
    backgroundColor: style.bg,
    color: style.text,
    "& .MuiChip-label": { px: "6px" },
  };
};

/* ── Timeline type config ────────────────────────────────────────────────── */
const timelineTypeConfig = {
  workOrder: {
    color: "#1E88E5",
    icon:  AssignmentTurnedInIcon,
    label: "Work Order",
  },
  comment: {
    color: "#43A047",
    icon:  ChatBubbleIcon,
    label: "Comment",
  },
  event: {
    color: "#8E24AA",
    icon:  EventNoteIcon,
    label: "Event",
  },
  email: {
    color: "#FB8C00",
    icon:  EmailIcon,
    label: "Email",
  },
  statusChange: {
    color: "#0891B2",
    icon:  SwapHorizIcon,
    label: "Status Change",
  },
};

const WorkOrderTimeline = ({ workOrderId }) => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/workOrderTimeline/${workOrderId}`);
        setTimelineData(response.data);
      } catch (err) {
        console.error("Error fetching timeline data:", err);
        setError("Failed to load timeline data.");
      } finally {
        setLoading(false);
      }
    };
    if (workOrderId) fetchTimeline();
  }, [workOrderId]);

  const combinedTimeline = useMemo(() => {
    if (!timelineData?.workOrder) return [];

    const { workOrder, comments = [], events = [], emails = [], statusHistory = [] } = timelineData;

    const timeline = [
      {
        type:  "workOrder",
        title: "Work Order Created",
        time:  workOrder.created_at,
        by:    workOrder.created_by_name || "System",
        email: workOrder.created_by_email || "",
      },

      ...comments.map(({ comment_text, sent_at, user_name, user_email }) => ({
        type:  "comment",
        title: comment_text,
        time:  sent_at || new Date().toISOString(),
        by:    user_name  || "Unknown User",
        email: user_email || "",
      })),

      ...events.map(({ title, created_at }) => ({
        type:  "event",
        title,
        time:  created_at || new Date().toISOString(),
      })),

      ...emails.map(({ subject, sent_at, email_address }) => ({
        type:  "email",
        title: subject,
        time:  sent_at || new Date().toISOString(),
        by:    email_address || "System Notification",
      })),

      ...statusHistory.map((h) => ({
        type:       "statusChange",
        title:      "Status Changed",
        time:       h.changed_at,
        by:         h.changed_by_name || "System",
        email:      h.changed_by_email || "",
        oldStatus:  h.old_status,
        newStatus:  h.new_status,
        comment:    h.comment_text  || null,
        attachment: h.upload_urls   || null,
      })),

      ...(workOrder.resolved_at
        ? [
            {
              type:  "workOrder",
              title: "Work Order Resolved",
              time:  workOrder.resolved_at,
              by:    workOrder.resolved_by_name || "System",
            },
          ]
        : []),
    ];

    return timeline.sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [timelineData]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "180px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error" sx={{ textAlign: "center" }}>{error}</Typography>;
  }

  if (combinedTimeline.length === 0) {
    return (
      <Card sx={{ ...pageCardSx, p: 2.5, textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No timeline activity found
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
          Timeline activity will appear here as the work order is updated.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: "#223142" }}>
        Work Order Timeline
      </Typography>

      <Box
        sx={{
          position: "relative",
          pl: { xs: 2, sm: 2.5 },
          "&::before": {
            content: '""',
            position: "absolute",
            left: { xs: "9px", sm: "11px" },
            top: 8,
            bottom: 8,
            width: "2px",
            backgroundColor: "rgba(47,99,173,0.12)",
          },
        }}
      >
        {combinedTimeline.map((item, index) => {
          const { type, title, time, by, email, oldStatus, newStatus, comment, attachment } = item;
          const config       = timelineTypeConfig[type] || timelineTypeConfig.workOrder;
          const IconComponent = config.icon;

          return (
            <motion.div
              key={`${type}-${time}-${index}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, delay: index * 0.03 }}
            >
              <Box sx={{ position: "relative", mb: 1.25 }}>
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: "-15px", sm: "-18px" },
                    top: 18,
                    width: { xs: 14, sm: 16 },
                    height: { xs: 14, sm: 16 },
                    borderRadius: "999px",
                    backgroundColor: config.color,
                    border: "3px solid #fff",
                    boxShadow: "0 0 0 1px rgba(47,99,173,0.08)",
                    zIndex: 2,
                  }}
                />

                <Card
                  sx={{
                    borderRadius: "14px",
                    border: type === "statusChange"
                      ? `1px solid ${config.color}30`
                      : "1px solid rgba(47,99,173,0.08)",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.04)",
                    backgroundColor: type === "statusChange" ? `${config.color}06` : "#fff",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: { xs: 1.1, sm: 1.25 } }}>
                    {/* Header row: title + type chip */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.9, minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            width: { xs: 30, sm: 32 },
                            height: { xs: 30, sm: 32 },
                            borderRadius: "9px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: `${config.color}15`,
                            flexShrink: 0,
                          }}
                        >
                          <IconComponent sx={{ color: config.color, fontSize: 16 }} />
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.25, wordBreak: "break-word", overflowWrap: "anywhere" }}
                          >
                            {/* For status changes show the transition inline */}
                            {type === "statusChange" && oldStatus
                              ? `${oldStatus} → ${newStatus}`
                              : title}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, width: { xs: "100%", sm: "auto" }, pl: { xs: "38px", sm: 0 } }}>
                        <Chip
                          size="small"
                          label={config.label}
                          sx={{
                            maxWidth: "100%",
                            height: 24,
                            borderRadius: "999px",
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            color: config.color,
                            backgroundColor: `${config.color}15`,
                            "& .MuiChip-label": { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", px: 1 },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* ── Status change: old → new chips ── */}
                    {type === "statusChange" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75, pl: "38px", flexWrap: "wrap" }}>
                        {oldStatus ? (
                          <Chip label={oldStatus} size="small" sx={statusChipSx(oldStatus)} />
                        ) : (
                          <Typography variant="caption" sx={{ color: "#9aa5b4", fontStyle: "italic" }}>Initial</Typography>
                        )}
                        <SwapHorizIcon sx={{ fontSize: 15, color: "#9aa5b4", flexShrink: 0 }} />
                        <Chip label={newStatus} size="small" sx={statusChipSx(newStatus)} />
                      </Box>
                    )}

                    {/* ── Comment body (for regular comment entries) ── */}
                    {type === "comment" && (
                      <Box sx={{ p: 1, borderRadius: "10px", backgroundColor: "#f9fafb", border: "1px solid rgba(47,99,173,0.06)", mb: 0.75 }}>
                        <Typography variant="body2" sx={{ color: "#555", fontStyle: "italic", fontSize: "0.82rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                          "{title}"
                        </Typography>
                      </Box>
                    )}

                    {/* ── Status-change optional comment ── */}
                    {type === "statusChange" && comment && (
                      <Box sx={{ p: 1, borderRadius: "10px", backgroundColor: "#f9fafb", border: "1px solid rgba(47,99,173,0.06)", mb: 0.75, ml: "38px" }}>
                        <Typography variant="body2" sx={{ color: "#555", fontStyle: "italic", fontSize: "0.82rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                          "{comment}"
                        </Typography>
                      </Box>
                    )}

                    {/* ── Status-change attachment ── */}
                    {type === "statusChange" && attachment && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75, ml: "38px" }}>
                        <AttachFileIcon sx={{ fontSize: 13, color: "#6b778c" }} />
                        <Typography
                          component="a"
                          href={attachment}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{ color: "#2f63ad", fontSize: "0.76rem", textDecoration: "underline", wordBreak: "break-all" }}
                        >
                          View attachment
                        </Typography>
                      </Box>
                    )}

                    {/* Non-comment / non-status-change body text */}
                    {type !== "comment" && type !== "statusChange" && (
                      <Typography variant="body2" sx={{ color: "#223142", mb: by || time ? 0.55 : 0, fontSize: "0.82rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {title}
                      </Typography>
                    )}

                    {/* ── By line ── */}
                    {by && (
                      <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.35, fontSize: "0.77rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {type === "email"
                          ? "To "
                          : type === "statusChange"
                          ? "Changed by "
                          : title === "Work Order Created"
                          ? "Created by "
                          : title === "Work Order Resolved"
                          ? "Resolved by "
                          : "By "}
                        <strong>{by}</strong>
                        {email ? ` (${email})` : ""}
                      </Typography>
                    )}

                    {/* ── Timestamp ── */}
                    <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "#8a94a6", fontStyle: "italic", display: "flex", alignItems: "center", mt: 0.25, wordBreak: "break-word" }}>
                      <AccessTimeIcon sx={{ fontSize: 11, mr: 0.45 }} />
                      {new Date(time).toLocaleString()}
                    </Typography>
                  </Box>
                </Card>
              </Box>
            </motion.div>
          );
        })}
      </Box>

      <Legend />
    </Box>
  );
};

const Legend = () => {
  const items = ["workOrder", "comment", "event", "email", "statusChange"];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142", mb: 0.75 }}>
        Legend
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {items.map((key) => {
          const item          = timelineTypeConfig[key];
          const IconComponent = item.icon;
          return (
            <Chip
              key={key}
              icon={<IconComponent sx={{ fontSize: "14px !important" }} />}
              label={item.label}
              size="small"
              sx={{
                borderRadius: "999px",
                fontWeight: 600,
                fontSize: "0.72rem",
                color: item.color,
                backgroundColor: `${item.color}15`,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default WorkOrderTimeline;
